/**
 * Share Modal Component
 *
 * Modal for sharing link analytics with other users. Allows Plus tier users
 * to invite others via email to view their link metrics.
 *
 * Sharing is now project-based only - users select a project to share all links within it.
 *
 * @module components/modals/share-modal
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  X,
  Loader2,
  Share2,
  Mail,
  Layers,
  AlertCircle,
  CheckCircle,
  Plus,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useUIStore } from '@/client/stores/ui-store';
import { apiFetch } from '@/client/lib/api';
import { useKeyboard } from '@/client/hooks/use-keyboard';
import { useFeatureGate } from '@/client/components/feature-gate';

interface ProjectOption {
  id: string;
  name: string;
  color: string;
  linkCount: number;
}

interface ShareResponse {
  id: string;
  status: 'pending' | 'active';
  inviteeEmail: string;
  inviteeName: string | null;
  scopeType: 'project';
  projectId: string | null;
  expiresAt: string | null;
  message: string;
}

export function ShareModal() {
  const { shareModalLink, closeShareModal } = useUIStore();
  const {
    hasAccess: hasSharing,
    openUpgradeModal,
    UpgradeModalElement,
  } = useFeatureGate('link_sharing', 'Share link analytics with teammates and stakeholders');
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [expiresInDays, setExpiresInDays] = useState<number | null>(null);
  const [result, setResult] = useState<ShareResponse | null>(null);

  // Fetch projects for Plus users
  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiFetch('/api/projects');
      if (!response.ok) return { data: [] };
      return response.json();
    },
    enabled: hasSharing && !!shareModalLink,
  });

  // Memoize projects to prevent unnecessary re-renders in dependent effects
  const projects: ProjectOption[] = useMemo(() => projectsData?.data || [], [projectsData?.data]);

  const shareMutation = useMutation({
    mutationFn: async (data: {
      inviteeEmail: string;
      scopeType: 'project';
      projectId: string;
      expiresInDays?: number;
    }) => {
      const response = await apiFetch('/api/shares', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Failed to create share');
      }
      return response.json() as Promise<ShareResponse>;
    },
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      toast.success(data.message);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    if (!selectedProjectId) {
      toast.error('Please select a project to share');
      return;
    }

    const data: {
      inviteeEmail: string;
      scopeType: 'project';
      projectId: string;
      expiresInDays?: number;
    } = {
      inviteeEmail: email.trim(),
      scopeType: 'project',
      projectId: selectedProjectId,
    };

    if (expiresInDays) {
      data.expiresInDays = expiresInDays;
    }

    shareMutation.mutate(data);
  };

  const handleClose = useCallback(() => {
    closeShareModal();
    setEmail('');
    setSelectedProjectId('');
    setExpiresInDays(null);
    setResult(null);
  }, [closeShareModal]);

  // Reset state when modal closes
  useEffect(() => {
    if (!shareModalLink) {
      setEmail('');
      setSelectedProjectId('');
      setExpiresInDays(null);
      setResult(null);
    }
  }, [shareModalLink]);

  // Pre-select project when opened from project context
  useEffect(() => {
    if (shareModalLink?.contextType === 'project' && projects.length > 0) {
      // Find the project that matches the ID passed in
      const matchingProject = projects.find((p) => p.id === shareModalLink.id);
      if (matchingProject) {
        setSelectedProjectId(matchingProject.id);
      }
    }
  }, [shareModalLink, projects]);

  // ⚡ Performance Optimization: Use shared keyboard hook instead of manual listener
  useKeyboard('Escape', handleClose, !!shareModalLink);

  if (!shareModalLink) return null;

  // Non-Plus users see upgrade prompt
  if (!hasSharing) {
    // Show upgrade modal and return null for the main content
    return (
      <>
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
          <div className="relative z-10 w-full max-w-md mx-4 bg-background rounded-lg shadow-xl border animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">Share Analytics</h2>
              <button
                onClick={handleClose}
                className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close share modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Share2 className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Upgrade to Plus</h3>
              <p className="text-muted-foreground mb-4">
                Sharing analytics is a Plus tier feature. Upgrade to share your link metrics with
                teammates and stakeholders.
              </p>
              <button
                onClick={openUpgradeModal}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors"
              >
                Upgrade to Plus
              </button>
            </div>
          </div>
        </div>
        {UpgradeModalElement}
      </>
    );
  }

  // Plus users without projects see prompt to create one
  if (!projectsLoading && projects.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
        <div className="relative z-10 w-full max-w-md mx-4 bg-background rounded-lg shadow-xl border animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold">Share Analytics</h2>
            <button
              onClick={handleClose}
              className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close share modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <Layers className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Create a Project First</h3>
            <p className="text-muted-foreground mb-4">
              Sharing is project-based. Create a project and add links to it, then you can share all
              the links in that project with others.
            </p>
            <Link
              to="/projects"
              onClick={handleClose}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Go to Projects
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-lg mx-4 bg-background rounded-lg shadow-xl border animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Share2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Share Project Analytics</h2>
              <p className="text-sm text-muted-foreground">Invite someone to view analytics</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close share modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Success State */}
        {result ? (
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {result.status === 'active' ? 'Share Created!' : 'Invitation Sent!'}
              </h3>
              <p className="text-muted-foreground">
                {result.status === 'active'
                  ? `${result.inviteeName || result.inviteeEmail} can now view your project's analytics.`
                  : `${result.inviteeEmail} will get access when they create an account.`}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="w-full px-4 py-2 rounded-md bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Project Selection */}
              <div className="space-y-2">
                <label htmlFor="project" className="text-sm font-medium">
                  Select project to share
                </label>
                {projectsLoading ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-muted/50">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="text-muted-foreground">Loading projects...</span>
                  </div>
                ) : (
                  <select
                    id="project"
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    required
                  >
                    <option value="">Choose a project...</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name} ({project.linkCount}{' '}
                        {project.linkCount === 1 ? 'link' : 'links'})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Warning Banner when project is selected */}
              {selectedProjectId &&
                (() => {
                  const selectedProject = projects.find((p) => p.id === selectedProjectId);
                  return selectedProject ? (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                      <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="font-medium text-amber-600">Sharing includes all links</p>
                        <p className="text-sm text-muted-foreground">
                          The invitee will see analytics for all{' '}
                          <span className="font-medium">{selectedProject.linkCount}</span>{' '}
                          {selectedProject.linkCount === 1 ? 'link' : 'links'} in &quot;
                          {selectedProject.name}&quot;, including any links added in the future.
                        </p>
                      </div>
                    </div>
                  ) : null;
                })()}

              {/* Email Input */}
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Invite by email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="colleague@company.com"
                    className="w-full pl-10 pr-4 py-2 rounded-md border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  They&apos;ll need to create an account to view analytics
                </p>
              </div>

              {/* Expiration (Optional) */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Access expiration (optional)</label>
                <select
                  value={expiresInDays ?? ''}
                  onChange={(e) =>
                    setExpiresInDays(e.target.value ? parseInt(e.target.value) : null)
                  }
                  className="w-full px-3 py-2 rounded-md border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="">Never expires</option>
                  <option value="7">7 days</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="365">1 year</option>
                </select>
              </div>

              {/* Info box */}
              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 text-sm">
                <AlertCircle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                <div className="text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">View-only access</p>
                  <p>
                    The person you invite will be able to view analytics but cannot edit or delete
                    anything. You can revoke access at any time from Settings.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 rounded-md border hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={shareMutation.isPending || !email.trim() || !selectedProjectId}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {shareMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4" />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
